<template>
  <div class="gauge-control-container">
    <p v-if="title" class="accent">{{ title }}</p>
    <div class="control-box" :class="{ disabled }">
      <p class="small dim">{{ minLabel }}</p>

      <div class="gauge-wrapper">
        <div class="tick-container">
          <slot></slot>
          <!--눈금자-->
          <div
            v-for="n in listValue"
            :key="n"
            class="tick"
            :style="{
              left: ((n - min) / (max - min)) * 100 + '%',
              display: n === min || n === max ? 'none' : 'block',
            }"
          ></div>
        </div>
        <!--컨트롤러-->
        <input
          type="range"
          :style="thumbStyle"
          :min="min"
          :max="max"
          :value="modelValue"
          :step="increment"
          @input="onInputGauge"
        />
      </div>

      <p class="small dim">{{ maxLabel }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, computed } from 'vue';

import { Gauge } from '@/types/components';

const props = withDefaults(defineProps<Gauge>(), {
  disabled: false,
  title: '',
  minLabel: 'Min',
  maxLabel: 'Max',
  min: 1,
  max: 10,
  increment: 1,
});

const emits = defineEmits(['update:modelValue']);
const globalThumbColor = inject('globalThumbColor', null);

const thumbStyle = computed(() => {
  return globalThumbColor ? `--thumb-color: ${globalThumbColor.value}` : '';
});

const listValue = computed(() => {
  const values: number[] = [];
  for (let i = props.min; i <= props.max; i += props.increment) {
    values.push(i);
  }
  return values;
});

const onInputGauge = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emits('update:modelValue', Number(target.value));
};
</script>

<style scoped lang="scss">
.gauge-control-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: fit-content;
  gap: 0.5rem;
  color: var(--font-default);

  .accent {
    font-size: 0.75rem;
  }
}
.control-box {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  width: 100%;
  border-radius: 10px;
  gap: 0.5rem;
}

.control-box.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.gauge-wrapper {
  position: relative;
  width: 100%;
}

.tick-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 20px);
  height: 50%;
  pointer-events: none;
}

.tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: var(--border-dark);
  opacity: 0.3;
}

input[type='range'] {
  width: 100%;
  height: 1.375rem;
  margin: 0;
  padding: 0.125rem;
  background: var(--surface-gray-dimmer);
  border: none;
  border-radius: 16px;
  appearance: none;
  -webkit-appearance: none;
  vertical-align: middle;
}

input[type='range']::-webkit-slider-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  background: var(--thumb-color, var(--surface-primary));
  -webkit-appearance: none;
  position: relative;
}

input:focus {
  box-shadow: none;
}
</style>
